import * as React from "react";

import { useSelector, useDispatch } from 'react-redux';
import { errMsg } from './registration-errors';
import { trackEvent } from '../analytics/tracking';
import PhoneInput,  { isPossiblePhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { sendVerificationCode } from './registration-utils';
import { setPhoneNumber } from '../actions/registrationActions';
import RegFlowHeader from './RegFlowHeader';
import ProcessRegistration from "./ProcessRegistration";
import DisplayModal from '../common/DisplayModal';
import EnterVerificationCode from './EnterVerificationCode';
import LogRegFlowAction from '../intercom/LogRegFlowAction';

import styles from './Registration.module.css';

/**
 * Ask for and validate phone number (RFC3966)
 */
const PhoneNumber = () => {
  
  const dispatch = useDispatch();
  // previously entered number from redux
  const current_number = useSelector(state => state.tel_number); 

  // For phone number input
  const [telNumber, setTelNumber] = React.useState(); 
  const [telNumberError, setTelNumberError] = React.useState();
  const [sendCodeError, setSendCodeError] = React.useState();
  const [verifyCodeError, setVerifyCodeError] = React.useState();
  // Verify Mode voice call option
  const [isVoiceCall, setIsVoiceCall] = React.useState(false);
  
  // Verify code modal
  const [showVerificationModal, setShowVerificationModal] = React.useState();
  // A reference to the number being verified
  const codeRecipientNumber = React.useRef();
  const countryCodeRef = React.useRef('US');

  // Complete registration
 const [submitRegistration, setSubmitRegistration] = React.useState(false);

  // Previously entered name, email, email_consent for reg flow abort logging
  // via LogRegFlowAction
  const name = useSelector(state => state.name);
  const email = useSelector(state => state.email);
  const okToEmail = useSelector(state => state.marketing_accepted);

  /**
   * Some extra complexity to support use of back button in reg flow
   * Parse out the country code and initialize the input
   */
  React.useEffect(() => {
    const telNumberFromProps = current_number ? parsePhoneNumber(current_number) : {};
    const { number, country:defaultCountryCode='US' } = telNumberFromProps; 
    countryCodeRef.current = defaultCountryCode;
    if (number && !telNumber) {
      setTelNumber(number);
    }
  },[current_number, telNumber]);

  const handleVerify = (event) => {
    event.preventDefault();
    setVerifyCodeError(false);
    // Handle missing input
    if (!telNumber && !current_number) {
      setTelNumberError(true);
      return;
    } else if (telNumber) {
      // This is the less strict validation to reduce false negatives
      // See: https://gitlab.com/catamphetamine/react-phone-number-input#isvalidphonenumbervalue-string-boolean
      const isValid = isPossiblePhoneNumber(telNumber); 
      setTelNumberError(!isValid);
      
      if (!isValid) {
        return;
      } else {
        const phoneNumberInfo = parsePhoneNumber(telNumber);
        
        // The country code has a tendency to get unset on back navigation so:
        countryCodeRef.current = phoneNumberInfo.country;
       
        const sendCodeCallback = ((response) => { 
          // success
          if(response.recipient_tel_number) {
            setShowVerificationModal(true);
          // failure
          } else {
            trackEvent('error_sending_SMS_code', 'reg_flow');
            setSendCodeError(true); 
          }
        });
        
        codeRecipientNumber.current = telNumber;
        const verificationInfo = {
          delivery_method: 'sms-text', 
          tel_number: codeRecipientNumber.current
        };
        const controller = new AbortController();
        
        // Send the SMS message or voice and show modal for code
        sendVerificationCode(verificationInfo, isVoiceCall, sendCodeCallback, controller);  
      }
    }
  }
  
  // The callback for the verification modal
  const onNumberVerfied = (response) => {
    // save phone number to redux and continue  
    if (response.success) {
      dispatch(setPhoneNumber(response.tel_number)); 
      // Hide verify modal so that submission errors can be seen
      setShowVerificationModal(false);
      // Process registration
      setSubmitRegistration(true);
      // Previously: navigateTop('/signUp/uploadID')
    } else {
      // something happened that was not success or error
      setVerifyCodeError(true);  
    }
  };

  // handle user input
  const onPhoneInputChange = (value) => {
    setTelNumber(value);
    setTelNumberError(false);
    setVerifyCodeError(false); 
  };

  const onVoiceCallClick = (event) => {
    const { checked:value } = event.currentTarget;
    setIsVoiceCall(value);
  } 

  // Add error styling if needed
  const inputWrapperStyles = telNumberError
  ? `${styles.phoneInputWrapper} ${styles.phoneInputError}`
  : styles.phoneInputWrapper; 
  
  return (
    <div className={styles.formOnlyPage}>
      <RegFlowHeader backLink="/signUp/signUp" pageTitle="Your Phone Number" />
      <div className={styles.regFlowForm}>
        <div className={styles.formHeader} data-testid="hdr">Can we get your digits?</div>
        <div className={styles.formInstructions}>Enter your mobile number</div>
        <form>
          <div className={inputWrapperStyles}>
            <PhoneInput
              id="tel_number"
              initialValueFormat="national"
              defaultCountry={countryCodeRef.current}
              placeholder="Enter phone number"
              value={telNumber}
              onChange={(value) =>{ onPhoneInputChange(value) }} />
            {/* elem id is for the test */}
            <div id="tel_number_err_msg" className={styles.phoneNumberErrorMsg }>
              {errMsg('tel_number',telNumberError)}
              {errMsg('send_code', sendCodeError)}
              {errMsg('verify_code', verifyCodeError)}
            </div>
          </div>
          { submitRegistration === false &&
            <>
              <button
                className={`${styles.formButton} ${styles.formSubmit}`}
                onClick={handleVerify}>
                { isVoiceCall
                  ? <>Verify via Voice Call</>
                  : <>Verify via SMS Message</>
                }
              </button>
              <div className={styles.verifyMethod}>
                <FormControlLabel
                  control={<Checkbox
                    color="default"
                    id="accept_terms"
                    checked={isVoiceCall}
                    onChange={onVoiceCallClick} />}
                  label="Send my verification code via voice call"
                />
              </div>
            </>
          }    
        </form>
      </div>

      {/* Intercom: Track that Reg Flow was started */}
      <LogRegFlowAction
        name={name}
        email={email}
        okToEmail={okToEmail} 
        actionKey="Registration_Start"
        actionValue={true} />
 
      { submitRegistration &&
        <ProcessRegistration />
      }

      { showVerificationModal &&
        <DisplayModal title="Verify Phone Number" closeFn={() => setShowVerificationModal(false)}>
          <EnterVerificationCode phoneNumber={codeRecipientNumber.current} 
            onVerified={onNumberVerfied}
            closeModalFn={() => setShowVerificationModal(false)} />
        </DisplayModal>
      }
    </div>
  );
}

export default PhoneNumber;
